import { lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from './hooks/useMediaQuery';

//Lazy (plan 5.1): react-joyride stays out of the entry chunk; desktop
//fetches it in parallel on mount, mobile (tours disabled) never does.
const Joyride = lazy(() => import('react-joyride'));

/**
 * Popper (through react-floater) measures a step against the target's nearest
 * scrolling ancestor, not against the screen. The Settings rail is
 * `overflow: auto` and 256px wide, so a 384px tooltip placed `right` of a rail
 * row does not "fit" — and floater's fallback chain for `right` is
 * ['right','bottom-end','top-end','left','top-start','bottom-start'], so it
 * settles on `bottom-end`, which right-aligns the tooltip to a 239px row and
 * puts its left edge — and the Next button — at -138px.
 *
 * Joyride already rewrites `preventOverflow` to the window when it detects a
 * custom scroll parent, but it never touches `flip`, which is the modifier that
 * chose the placement. So pin both to the viewport: that is the only boundary
 * the user can actually see. Steps stay free to override — joyride merges
 * defaults < Joyride props < step.floaterProps.
 *
 * Exported because four tours render Joyride directly rather than through this
 * portal: CommonThead, DashGrid, AddEditInventory, AddEditCommitted.
 */
export const TOUR_FLOATER_PROPS = {
    options: {
        flip: { boundariesElement: 'viewport' },
        preventOverflow: { boundariesElement: 'viewport' },
    },
};

export default function JoyridePortal({ children, floaterProps, ...joyrideProps }) {
    //Tours are desktop-only: their steps target desktop-strip elements
    //(#tab-*, floating buttons) that don't exist in the mobile shell.
    const isMobile = useIsMobile();
    if (isMobile) return null;

    const mergedFloaterProps = {
        ...TOUR_FLOATER_PROPS,
        ...floaterProps,
        options: { ...TOUR_FLOATER_PROPS.options, ...floaterProps?.options },
    };

    // Render Joyride in a portal at the end of document.body
    return createPortal(
        <Suspense fallback={null}>
            <Joyride {...joyrideProps} floaterProps={mergedFloaterProps} />
        </Suspense>,
        document.body
    );
}
